/**
 * Single rule group - OR of tags
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

import { TagAddPopover } from './TagAddPopover';

export interface RuleBuilderGroupProps {
  groupIndex: number;
  tags: string[];
  availableTags: string[];
  tagColors?: Record<string, string>;
  onTagsChange: (tags: string[]) => void;
  onRemove: () => void;
  canRemove: boolean;
}

export const RuleBuilderGroup: FC<RuleBuilderGroupProps> = ({
  groupIndex,
  tags,
  availableTags,
  tagColors,
  onTagsChange,
  onRemove,
  canRemove,
}) => {
  const addOptions = availableTags.filter((t) => !tags.includes(t));

  const addTag = (tag: string) => {
    onTagsChange([...tags, tag]);
  };

  const removeTag = (tag: string) => {
    onTagsChange(tags.filter((t) => t !== tag));
  };

  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          Group {groupIndex} · any of these tags
        </span>
        {canRemove && (
          <Button variant="ghost" size="sm" className="h-6 text-destructive hover:text-destructive" onClick={onRemove}>
            Remove
          </Button>
        )}
      </div>
      <div className="flex min-h-[36px] flex-wrap items-center gap-2">
        {tags.length === 0 ? (
          <span className="text-xs italic text-muted-foreground">Add at least one tag</span>
        ) : (
          tags.map((tag) => {
            const color = tagColors?.[tag];
            return (
              <span
                key={tag}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium',
                  color ? 'text-white' : 'bg-muted text-foreground',
                )}
                style={color ? { backgroundColor: color } : undefined}
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="rounded hover:bg-black/20"
                  aria-label={`Remove ${tag}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })
        )}
        {addOptions.length > 0 && (
          <TagAddPopover
            options={addOptions}
            tagColors={tagColors}
            onSelect={addTag}
          />
        )}
      </div>
    </div>
  );
};
